// --- React & Redux
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
// --- External UI Libraries
import { Row, Col } from "antd";
import Select from "@dtci/spellbook/Select";
import DataGrid from "@dtci/spellbook/DataGrid";
import Input from "@dtci/spellbook/Input";
// --- Components
import { InputLabel } from "tap/components/InputLabel";
// --- Hooks & Actions
import { useAgGridApi } from "tap/components/AGGrid/hooks.js";
import { useJobTasksData } from "../../../../hooks/useJobTasksData";
import { useJobsActions } from "tap/store/tapJobs/useJobs.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
// --- Utils
import { tapApi } from "tap/api.js";
import { getItemRange } from "tap/utils/pagination.js";
import { useJobTasksGridUtils } from "./utils/gridUtils";
// --- Styles
import styles from "../../../../../../components/AGGrid/details-table.module.scss";

/**
 * Job Tasks Grid
 *
 * Features:
 * - Displays a data grid of job tasks
 * - Supports filtering by Job ID and Task Status
 * - Integrates with Redux for state management
 *
 * @returns {JSX.Element}
 */
export const JobTasksGrid = () => {
  const agGridApi = useAgGridApi();
  const {
    isTasksModalOpen,
    taskFilterForm,
    jobTasks,
    jobTasksDefaultFilterRange,
    jobStatusOptions,
    jobTasksCurrentPage,
    jobTasksItemsPerPage,
  } = useSelector((state) => state.tapJobs);

  const { fetchJobTasks, updateJobs } = useJobTasksData();
  const { updateJobsSearchForm } = useJobsActions();
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const { columns } = useJobTasksGridUtils({
    tapApi,
    taskFilterForm,
    jobTasksDefaultFilterRange,
    updateJobs,
    handlers: {
      setLoadingTrue,
      setLoadingFalse,
      columnHandlers: [],
    },
  });

  // Fetch job tasks when modal opens
  useEffect(() => {
    if (!isTasksModalOpen) return;
    loadJobTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTasksModalOpen]);

  // Fetch job tasks when filter form changes (triggered by user action)
  useEffect(() => {
    if (!isTasksModalOpen) return;
    loadJobTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskFilterForm]);

  const loadJobTasks = async () => {
    const range = getItemRange(jobTasksCurrentPage, jobTasksItemsPerPage);
    const data = await fetchJobTasks(range);
    if (data) {
      updateJobs({
        jobTasks: data?.content,
        jobTasksTotalPages: data?.totalPages,
        jobTasksTotalItems: data?.totalElements,
        jobTasksCurrentPage: data?.pageable?.pageNumber,
      });
    }
  };

  const handleInputChange = (field, value) => {
    updateJobsSearchForm({
      key: "taskFilterForm",
      data: { [field]: value },
    });
  };

  return (
    <>
      <Row>
        <Col>
          <InputLabel colProps={{ span: 24 }} label="Job ID">
            <Input
              value={taskFilterForm.jobId}
              onChange={(e) => handleInputChange("jobId", e.target.value)}
              width="medium"
              id="jobId"
              name="jobId"
              placeholder="Enter"
            />
          </InputLabel>
        </Col>
        <Col>
          <InputLabel colProps={{ span: 24 }} label="Task Status">
            <Select
              options={jobStatusOptions}
              isClearable
              isSearchable
              onChange={(value) => handleInputChange("taskStatus", value)}
              value={taskFilterForm.taskStatus}
            />
          </InputLabel>
        </Col>
      </Row>
      <DataGrid
        className={styles.dataGrid}
        autoSizing
        agGrid={{
          ref: agGridApi.AgGridApiRef,
          rowData: jobTasks ?? [],
          suppressColumnStateReset: true,
          headerHeight: 45,
          defaultColDef: {
            wrapText: true,
            autoHeight: true,
            resizable: true,
            suppressHeaderMenuButton: true,
            cellStyle: {
              paddingTop: "10px",
              paddingBottom: "10px",
              wordBreak: "break-word",
            },
          },
        }}
        bordered="none"
        columnDefs={columns}
        sortable={false}
        striped
        noResultsMessage={"No Job Tasks Found"}
      />
    </>
  );
};
